import { PausaComMotivo } from '../components/PausaComMotivo';
import {
  OPERATIONAL_STATE_LABEL,
  OPERATIONAL_STATE_EXPLICACAO,
  type OperationalState,
  type SystemState,
} from '../lib/navigation';
import { formatInstant } from '../lib/format';
import type { OperationsResponse } from '../lib/api';

/**
 * Master Switch: a 2a area do layout entregue em 17/09/2026.
 *
 * Ate aqui a parada de emergencia existia so como botao no cabecalho. O handoff
 * do cliente pede uma tela, e diz o que ela precisa sustentar:
 *
 *   "Pausa deve ser facil. Retomar exige Safe Resume.
 *    Nunca executar backlog acumulado cegamente."
 *
 * As tres frases viraram as tres secoes desta tela, nesta ordem.
 *
 * Nada aqui e estado de front-end: o que esta parado sai de `active_pauses`, que
 * o servidor deriva do `PauseState`. Um interruptor que guardasse o proprio
 * estado mostraria "ON" numa aba enquanto o sistema esta parado na outra.
 */

/**
 * As categorias que o servidor aceita pausar.
 *
 * A lista e ESCRITA aqui porque o rotulo em portugues e decisao de interface, e
 * nao dado: o servidor devolve `community_auto_send`, que nao e o que se mostra
 * para quem opera. O que impede as duas listas de divergirem em silencio e um
 * teste de contrato que compara estas chaves com as do servidor.
 *
 * O mockup do pacote desenha CINCO linhas; o sistema tem seis, porque
 * `integration` tambem e pausavel. Entregar as cinco do desenho esconderia uma
 * alavanca real de operacao, entao vale o que existe.
 */
export const CATEGORIAS_PAUSAVEIS = [
  {
    scope: 'publication',
    label: 'Publicação orgânica',
    efeito: 'Nada novo é publicado. O que já está no ar continua no ar.',
  },
  {
    scope: 'paid_media',
    label: 'Mídia paga',
    efeito: 'Nenhuma campanha sobe, ativa ou gasta. As que estão no ar são pausadas na Meta.',
  },
  {
    scope: 'digital_twin',
    label: 'Digital Twin',
    efeito: 'Nenhum vídeo é gerado no HeyGen. A cota do mês para de ser consumida.',
  },
  {
    scope: 'community_auto_send',
    label: 'Resposta automática da comunidade',
    efeito: 'Nenhuma resposta sai sozinha. A classificação e a fila humana continuam.',
  },
  {
    scope: 'sales_auto_send',
    label: 'Follow-up de vendas',
    efeito: 'Nenhum follow-up é enviado. Os prazos continuam sendo contados.',
  },
  {
    scope: 'integration',
    label: 'Integrações',
    efeito: 'Nenhuma sincronização externa acontece. A leitura de estado continua.',
  },
] as const;

export function MasterSwitch({
  operations,
  systemState,
  motorEnabled,
  canPause,
  canPauseCategory,
}: {
  readonly operations: OperationsResponse;
  readonly systemState: SystemState;
  readonly motorEnabled: boolean;
  readonly canPause: boolean;
  readonly canPauseCategory: boolean;
}) {
  const sistemaPausado = systemState === 'paused';
  /*
    O estado que a tela AFIRMA, derivado no servidor. `systemState` continua
    decidindo o BOTAO (parar ou retomar), porque esse eixo e o de pausa; o que
    a pessoa LE e outro, e dizia "ON / Operacao ativa" num ambiente de teste.
  */
  const estado: OperationalState = operations.operational_state;
  const operando = estado === 'running';

  /*
    `active_pauses` traz a pausa de sistema junto com as de categoria. O indice
    e por escopo porque a tabela pergunta "esta categoria esta parada?", e
    varrer a lista por linha daria o mesmo resultado com mais chance de erro.
  */
  const pausaPorEscopo = new Map(operations.active_pauses.map((p) => [p.scope, p]));
  const pausaDoSistema = pausaPorEscopo.get('system');

  return (
    <section className="max-w-5xl" aria-labelledby="titulo-master-switch">
      <h1 id="titulo-master-switch" className="text-xl font-semibold">
        Master Switch
      </h1>
      <p className="mt-1 text-sm text-ink-muted">
        Pare todas as ações externas sem perder dado, estado ou histórico.
      </p>

      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        {/* 1. Pausa deve ser facil. */}
        <div className="rounded-card border border-line bg-card p-4 shadow-card">
          <h2 className="text-xs uppercase tracking-wider text-ink-muted">Estado do sistema</h2>
          <p
            className={`mt-1 text-4xl font-extrabold ${
              sistemaPausado ? 'text-danger' : operando ? 'text-ok' : 'text-accent'
            }`}
            data-testid="estado-master"
          >
            {sistemaPausado ? 'PARADO' : operando ? 'ON' : 'EM TESTE'}
          </p>
          {/*
            O rotulo por extenso acompanha a palavra grande porque `ON` sozinho
            nao distingue "em operacao" de "parcialmente pausado", que sao
            estados diferentes e nenhum dos dois e "pausado".
          */}
          <p className="text-sm text-ink-muted">
            Sistema {OPERATIONAL_STATE_LABEL[estado]}. {OPERATIONAL_STATE_EXPLICACAO[estado]}
          </p>
          {!motorEnabled && (
            /*
              O motor dirige a cadeia. Desligado, nenhuma fila existe e nenhum
              pipeline anda, e isso NAO aparecia em lugar nenhum da tela: o
              sistema podia estar "sem pausa" com nada se movendo por dentro.
            */
            <p
              className="mt-2 rounded border border-warn-border bg-warn-surface p-2 text-xs text-warn-ink"
              data-testid="motor-desligado"
            >
              O motor está desligado por configuração. Nenhuma fila foi criada e nenhum pipeline é
              dirigido, então nada anda sozinho aqui dentro.
            </p>
          )}

          <p className="mt-3 text-sm text-ink-muted">
            Ao pausar, publicação, mídia paga, respostas, follow-ups, geração de vídeo e
            sincronizações externas param. Log, estado, webhook e monitoramento continuam: pausar
            não apaga nem perde nada.
          </p>

          {pausaDoSistema && (
            <p
              className="mt-3 rounded border border-danger-border bg-danger-surface p-3 text-sm text-danger-ink"
              data-testid="motivo-da-pausa"
            >
              <span className="font-semibold">
                Parado desde {formatInstant(pausaDoSistema.paused_at)}.
              </span>{' '}
              {pausaDoSistema.reason}
            </p>
          )}

          <div className="mt-4">
            {canPause ? (
              <PausaComMotivo
                pausado={sistemaPausado}
                commandType={{ parar: 'PauseSystem', retomar: 'ResumeSystem' }}
                targetRef={{ object_type: 'System', object_id: 'system' }}
                payload={{ scope: 'system', scope_id: null }}
                /*
                  O rotulo acompanha o que existe para desligar.

                  "Desligar operacao" com o sistema em teste e a mesma mentira
                  em miniatura: nao ha operacao. Parar continua valendo e
                  continua sendo util (tranca tudo e exige Safe Resume para
                  voltar), entao o que muda e a palavra, nao o botao.
                */
                rotuloParar={operando ? 'Desligar operação' : 'Parar o sistema'}
                rotuloRetomar="Retomar operação"
                explicacaoAoParar="Nenhum efeito externo novo acontece enquanto o sistema estiver pausado: nada publica, nada envia, nada gasta. O que já está no ar continua no ar."
                explicacaoAoRetomar="Retomar NÃO religa nada sozinho: a verificação de prontidão roda antes, e o que estava na fila é revalidado item a item."
                idDoCampo="master"
              />
            ) : (
              <p className="text-sm text-ink-faint" data-testid="sem-escopo-pausa">
                Seu perfil não pode parar nem retomar o sistema. Quem pode: quem tem os escopos de
                pausa e retomada.
              </p>
            )}
          </div>
        </div>

        {/* 2. Retomar exige Safe Resume. 3. Nunca executar backlog cegamente. */}
        <div className="rounded-card border border-line bg-card p-4 shadow-card">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
            Safe Resume
          </h2>
          <p className="mt-2 text-sm text-ink-muted">
            Retomar não é o inverso de pausar. Antes de qualquer efeito externo voltar, o servidor
            roda a verificação de prontidão e cada item responde uma de três coisas: passou,
            reprovou, ou não deu para medir. Reprovação segura a retomada e a tela mostra qual item
            barrou.
          </p>

          <h3 className="mt-4 text-sm font-semibold">O que acontece com o que ficou parado</h3>
          <p className="mt-1 text-sm text-ink-muted">
            O acumulado durante a pausa não é executado de uma vez. Cada item volta para uma fila
            própria de revalidação, que decide um a um se ele ainda vale, se expirou, se é para
            cancelar ou se precisa de decisão humana, e grava o motivo. Uma pausa longa não vira uma
            enxurrada de publicação no minuto seguinte à retomada.
          </p>

          <p className="mt-4 rounded-[11px] border border-line bg-surface-sunken p-3 text-xs text-ink-muted">
            Não existe caminho de forçar a retomada por cima de um bloqueio. Na reprovação, a saída
            é resolver o que está bloqueando, e não passar por cima.
          </p>
        </div>
      </div>

      {/* Pausas por categoria: parar uma coisa sem parar tudo. */}
      <h2 className="mt-6 text-xs font-semibold uppercase tracking-wider text-ink-muted">
        Pausas por categoria
      </h2>
      <p className="mt-1 text-sm text-ink-muted">
        Parar uma categoria deixa o resto do sistema de pé. É o que se usa quando o problema é de um
        canal só.
      </p>

      <div className="mt-3 overflow-x-auto rounded-card border border-line bg-card shadow-card">
        <table className="w-full text-left text-sm">
          <caption className="sr-only">
            Categorias que podem ser pausadas isoladamente, com o estado atual de cada uma
          </caption>
          <thead>
            <tr className="border-b border-line">
              <th
                scope="col"
                className="px-4 py-2.5 text-xs uppercase tracking-wide text-ink-faint"
              >
                Categoria
              </th>
              <th
                scope="col"
                className="px-4 py-2.5 text-xs uppercase tracking-wide text-ink-faint"
              >
                Estado
              </th>
              <th
                scope="col"
                className="px-4 py-2.5 text-xs uppercase tracking-wide text-ink-faint"
              >
                Ação
              </th>
            </tr>
          </thead>
          <tbody>
            {CATEGORIAS_PAUSAVEIS.map((categoria) => {
              const pausa = pausaPorEscopo.get(categoria.scope);
              const pausada = pausa !== undefined;
              /*
                TRES estados, e nao dois.

                "ATIVA" era dito para toda categoria sem pausa, inclusive as que
                estao DESLIGADAS por configuracao. No ambiente de teste isso
                fazia as seis linhas aparecerem verdes escritas "ATIVA" com
                nenhum canal capaz de publicar, gastar ou enviar.

                O interruptor e o sinal honesto: ele ja exige a flag da
                funcionalidade E um provedor real escolhido. `integration` nao
                tem interruptor (so pausa), entao para ela a pergunta nao se
                aplica e a linha diz isso.
              */
              const interruptor = operations.external_effect_switches[categoria.scope];
              const estadoDaLinha = pausada
                ? { texto: 'PAUSADA', tom: 'border-warn-border bg-warn-surface text-warn-ink' }
                : interruptor === true
                  ? { texto: 'LIGADA', tom: 'border-ok-border bg-ok-surface text-ok-ink' }
                  : interruptor === false
                    ? {
                        texto: 'DESLIGADA',
                        tom: 'border-line bg-surface-sunken text-ink-muted',
                      }
                    : { texto: 'SEM PAUSA', tom: 'border-line bg-surface-sunken text-ink-muted' };
              return (
                <tr
                  key={categoria.scope}
                  className="border-b border-line last:border-b-0 align-top"
                  data-testid={`linha-${categoria.scope}`}
                >
                  <td className="px-4 py-3">
                    <span className="font-semibold">{categoria.label}</span>
                    <span className="block text-xs text-ink-muted">{categoria.efeito}</span>
                    {pausa && (
                      <span className="mt-1 block text-xs text-danger-ink">
                        Parada desde {formatInstant(pausa.paused_at)}: {pausa.reason}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {/*
                      O estado e TEXTO dentro de uma etiqueta colorida, e nao a
                      cor sozinha: o handoff exige nao depender apenas de cor.
                    */}
                    <span
                      className={`inline-block rounded-full border px-2 py-0.5 text-xs font-semibold ${estadoDaLinha.tom}`}
                      data-testid={`estado-${categoria.scope}`}
                    >
                      {estadoDaLinha.texto}
                    </span>
                    {!pausada && interruptor === false && (
                      <span className="mt-1 block text-[11px] text-ink-faint">
                        desligada por configuração, não por pausa
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {canPauseCategory ? (
                      <PausaComMotivo
                        pausado={pausada}
                        commandType={{ parar: 'PauseCategory', retomar: 'ResumeCategory' }}
                        targetRef={{ object_type: 'PauseState', object_id: categoria.scope }}
                        payload={{ scope: categoria.scope, scope_id: null }}
                        rotuloParar="Pausar"
                        rotuloRetomar="Reativar"
                        explicacaoAoParar={categoria.efeito}
                        explicacaoAoRetomar="A categoria volta a operar. O que ficou parado nela passa pela mesma revalidação item a item."
                        idDoCampo={categoria.scope}
                      />
                    ) : (
                      <span className="text-xs text-ink-faint">Sem permissão</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
